import { Extension } from "@tiptap/core";

export interface BlockDefaultFontOptions {
  types: string[];
}

/**
 * Per-block default font family/size — a fallback for text with no explicit
 * FontFamily/FontSize mark, rendered as inline style on the paragraph/heading
 * element itself. An explicit per-run mark (FontFamilyPicker/FontSizePicker,
 * both marks on `textStyle`) still wins: its own inline `<span style>` beats
 * an ancestor's inherited style, same as normal CSS cascade.
 *
 * This exists because marks can't be set on a genuinely empty paragraph (no
 * text run to carry them) — see convert.ts's paragraphsToPmDoc, which sets
 * these attrs on a brand-new document's synthesized empty paragraph from
 * Settings > General's default font/size, so typed text inherits it
 * immediately without ever touching the toolbar.
 */
export const BlockDefaultFont = Extension.create<BlockDefaultFontOptions>({
  name: "blockDefaultFont",

  addOptions() {
    return { types: [] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          defaultFontFamily: {
            default: null as string | null,
            parseHTML: (element: HTMLElement) => element.style.fontFamily || null,
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.defaultFontFamily) return {};
              return { style: `font-family: ${attributes.defaultFontFamily}` };
            },
          },
          defaultFontSize: {
            default: null as string | null,
            parseHTML: (element: HTMLElement) => element.style.fontSize || null,
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.defaultFontSize) return {};
              return { style: `font-size: ${attributes.defaultFontSize}` };
            },
          },
        },
      },
    ];
  },
});
